import React, { useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './anchor-styles.css';

/**
 * Analytics Charts Component
 */
const AnalyticsCharts = ({ analyticsData = {}, loading }) => {
  const [activeTab, setActiveTab] = useState('trend');

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="text-center" style={{ padding: '50px' }}>
          <i className="fa fa-spinner fa-spin fa-3x"></i>
          <p style={{ marginTop: '15px' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Sample data - Replace with actual data from props
  const monthlyTrendData = analyticsData.monthlyTrend || [
    { month: 'Sep', amount: 8500000, count: 32 },
    { month: 'Oct', amount: 10200000, count: 38 },
    { month: 'Nov', amount: 14500000, count: 45 },
    { month: 'Dec', amount: 12800000, count: 42 },
    { month: 'Jan', amount: 11000000, count: 35 },
    { month: 'Feb', amount: 12500000, count: 40 }
  ];

  const topSuppliers = analyticsData.topSuppliers || [
    { name: 'ABC Suppliers', amount: 12500000 },
    { name: 'XYZ Industries', amount: 8200000 },
    { name: 'DEF Enterprises', amount: 5900000 },
    { name: 'GHI Corporation', amount: 3100000 },
    { name: 'JKL Traders', amount: 2800000 }
  ];

  const statusDistribution = analyticsData.statusDistribution || [
    { name: 'Approved', value: 78, count: 312 },
    { name: 'Pending', value: 15, count: 60 },
    { name: 'Rejected', value: 5, count: 20 },
    { name: 'Others', value: 2, count: 8 }
  ];

  const COLORS = {
    Approved: '#4CAF50',
    Pending: '#FF9800',
    Rejected: '#F44336',
    Others: '#9E9E9E'
  };

  return (
    <div className="analytics-charts">
      <div className="analytics-header">
        <h4 style={{ marginBottom: '20px', fontWeight: '600' }}>
          <i className="fa fa-bar-chart"></i> Analytics & Insights
        </h4>
      </div>

      {/* Tab Navigation */}
      <ul className="nav nav-tabs" style={{ marginBottom: '20px' }}>
        <li className={activeTab === 'trend' ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('trend'); }}>
            Monthly Trend
          </a>
        </li>
        <li className={activeTab === 'supplier' ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('supplier'); }}>
            Supplier-wise
          </a>
        </li>
        <li className={activeTab === 'status' ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('status'); }}>
            Status Distribution
          </a>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Monthly Trend Chart */}
        {activeTab === 'trend' && (
          <div className="chart-container">
            <h5 style={{ marginBottom: '20px', fontWeight: '600' }}>
              Monthly Invoice Approval Trend (Last 6 Months)
            </h5>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={monthlyTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis 
                  tickFormatter={(value) => `₹${(value / 10000000).toFixed(1)}Cr`}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  stroke="#4CAF50"
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'amount') {
                      return [`₹${(value / 10000000).toFixed(2)} Cr`, 'Amount'];
                    }
                    return [value, 'Count'];
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#1976D2" 
                  strokeWidth={3}
                  dot={{ fill: '#1976D2', r: 5 }}
                  activeDot={{ r: 8 }}
                  name="Approved Amount"
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#4CAF50" 
                  strokeWidth={2}
                  dot={{ fill: '#4CAF50', r: 4 }}
                  name="Invoice Count"
                  yAxisId="right"
                />
              </LineChart>
            </ResponsiveContainer>

            <div className="chart-insights" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
              <h6 style={{ fontWeight: '600', marginBottom: '10px' }}>Key Insights:</h6>
              <ul style={{ marginBottom: '0' }}>
                <li>Highest approval month: November with ₹14.5 Cr</li>
                <li>Average monthly approval: ₹11.58 Cr</li>
                <li>Total invoices processed: 232</li>
              </ul>
            </div>
          </div>
        )}

        {/* Supplier-wise Chart */}
        {activeTab === 'supplier' && (
          <div className="row">
            <div className="col-md-6">
              <div className="chart-container">
                <h5 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  Top 5 Suppliers by Amount
                </h5>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={topSuppliers} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      tickFormatter={(value) => `₹${(value / 10000000).toFixed(1)}Cr`}
                    />
                    <YAxis type="category" dataKey="name" width={150} />
                    <Tooltip 
                      formatter={(value) => `₹${(value / 10000000).toFixed(2)} Cr`}
                    />
                    <Bar dataKey="amount" fill="#1976D2" name="Total Approved">
                      {topSuppliers.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(210, 70%, ${70 - index * 10}%)`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="col-md-6">
              <div className="supplier-ranking">
                <h5 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  Supplier Rankings
                </h5>
                <div className="ranking-list">
                  {topSuppliers.map((supplier, index) => (
                    <div key={index} className="ranking-item">
                      <div className="rank-number">{index + 1}</div>
                      <div className="rank-info">
                        <div className="rank-name">{supplier.name}</div>
                        <div className="rank-amount">
                          ₹{(supplier.amount / 10000000).toFixed(2)} Cr
                        </div>
                      </div>
                      <div className="rank-percentage">
                        {((supplier.amount / topSuppliers[0].amount) * 100).toFixed(0)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Distribution Chart */}
        {activeTab === 'status' && (
          <div className="row">
            <div className="col-md-6">
              <div className="chart-container">
                <h5 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  Invoice Status Distribution
                </h5>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}%`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="col-md-6">
              <div className="status-breakdown">
                <h5 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  Status Breakdown
                </h5>
                <div className="status-list">
                  {statusDistribution.map((status, index) => (
                    <div key={index} className="status-item">
                      <div 
                        className="status-color" 
                        style={{ backgroundColor: COLORS[status.name] }}
                      ></div>
                      <div className="status-details">
                        <div className="status-name">{status.name}</div>
                        <div className="status-stats">
                          <span className="status-count">{status.count} invoices</span>
                          <span className="status-percent">{status.value}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="status-summary" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#E3F2FD', borderRadius: '8px' }}>
                  <h6 style={{ fontWeight: '600', marginBottom: '10px' }}>Summary:</h6>
                  <ul style={{ marginBottom: '0' }}>
                    <li>Total Invoices: 400</li>
                    <li>Approval Rate: 78%</li>
                    <li>Pending Review: 60 invoices</li>
                    <li>Rejection Rate: 5%</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="analytics-footer" style={{ marginTop: '30px', textAlign: 'center' }}>
        <button className="btn btn-primary">
          <i className="fa fa-bar-chart"></i> View Detailed Reports
        </button>
        <button className="btn btn-default" style={{ marginLeft: '10px' }}>
          <i className="fa fa-download"></i> Export Data
        </button>
      </div>
    </div>
  );
};

export default AnalyticsCharts;

